import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Sparkles, Quote, ArrowRight, ArrowUpRight, Instagram, Facebook } from "lucide-react";
import { Reveal } from "../components/Reveal";
import AssessmentForm from "../components/AssessmentForm";
import { PostBadge, RoyalRule } from "../components/PostBadge";
import { ShareBar } from "../components/ShareBar";
import { CrownMark } from "../components/CrownMark";
import { CrownWatermark } from "../components/CrownWatermark";
import { ShowcaseFigure } from "../components/ShowcaseFigure";
import { postBySlug } from "../data/blog";
import { IMAGES, SERVICE_AREAS } from "../data/content";
import { INSTAGRAM_HANDLE, INSTAGRAM_URL, FACEBOOK_URL } from "../data/contact";
import Seo from "../components/Seo";
import { responsive } from "../lib/img";
import api from "../lib/api";

const DOMAIN = "https://www.speedyresurfacing.com.au";

const Block = ({ b, post }) => {
  if (b.type === "assess")
    return (
      <section id={b.id} className="scroll-mt-32 mt-16" data-testid={`blog-assess-${b.n}`}>
        <div className="flex items-end gap-5 border-t border-[color:var(--rule)] pt-8">
          <span aria-hidden className="font-heading font-black text-6xl md:text-7xl leading-[0.8] text-transparent [-webkit-text-stroke:1.5px_var(--accent)] opacity-90 select-none">{b.n}</span>
          <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight leading-tight">{b.text}</h2>
        </div>
        <div className="mt-6 border-l-2 border-[color:var(--accent)] pl-6 md:pl-8">
          {b.paras.map((t) => <p key={t.slice(0, 40)} className="mt-4 first:mt-0 text-[17px] md:text-lg leading-[1.85] text-zinc-200">{t}</p>)}
          {b.items && (
            <ul className="mt-6 space-y-4">
              {b.items.map((it) => (
                <li key={it.t} className="flex gap-4">
                  <span className="mt-2.5 h-1.5 w-6 shrink-0 bg-[color:var(--accent)]" />
                  <span className="text-[17px] md:text-lg leading-[1.75] text-zinc-200">{it.b && <strong className="font-heading font-bold text-white">{it.b}: </strong>}{it.t}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {b.image && (
          <ShowcaseFigure src={IMAGES[b.image]} alt={b.caption} caption={b.caption} wide={!!post.showcase} testid={`blog-assess-figure-${b.n}`} />
        )}
      </section>
    );
  if (b.type === "h2")
    return (
      <h2 id={b.id} className="scroll-mt-32 mt-16 border-t border-[color:var(--rule)] pt-8 font-heading font-black text-2xl md:text-3xl tracking-tight">
        {b.text}
      </h2>
    );
  if (b.type === "p")
    return (
      <p className={`mt-6 text-[17px] md:text-lg leading-[1.85] text-zinc-200 ${
        b.lead
          ? "first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:font-heading first-letter:text-6xl first-letter:font-black first-letter:leading-[0.75] first-letter:text-gold"
          : ""
      }`}>
        {b.text}
      </p>
    );
  if (b.type === "ul")
    return (
      <ul className="mt-7 space-y-4">
        {b.items.map((it) => (
          <li key={it.t} className="flex gap-4">
            <span className="mt-2.5 h-1.5 w-6 shrink-0 bg-[color:var(--accent)]" />
            <span className="text-[17px] md:text-lg leading-[1.75] text-zinc-200">
              {it.b && <strong className="font-heading font-bold text-white">{it.b}: </strong>}
              {it.t}
            </span>
          </li>
        ))}
      </ul>
    );
  if (b.type === "quote")
    return (
      <blockquote className="relative my-12 border-l-2 border-[color:var(--accent)] bg-[color:var(--accent-soft)] py-8 pl-8 pr-6">
        <Quote className="absolute right-5 top-5 text-gold/15" size={54} />
        <p className="font-heading font-black text-xl md:text-3xl leading-tight tracking-tight text-white">{b.text}</p>
        <span className="mt-4 block font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Speedy Resurfacing</span>
      </blockquote>
    );
  if (b.type === "image")
    return (
      <ShowcaseFigure src={IMAGES[b.src || post.inlineImage]} alt={b.caption || post.inlineImageCaption} caption={b.caption || post.inlineImageCaption}
                      natural={!!b.natural} wide={!!post.showcase} testid="blog-figure" />
    );
  if (b.type === "gallery" && post.showcase)
    return (
      <figure className="my-14 lg:-mr-24 xl:-mr-40" data-testid="blog-gallery">
        <div className={`grid gap-4 ${b.images.length === 2 ? "grid-cols-1 sm:grid-cols-2" : b.images.length === 3 ? "md:grid-cols-3" : "grid-cols-2 md:grid-cols-4"}`}>
          {b.images.map((k, i) => (
            <div key={k} className="group relative overflow-hidden rounded-xl ring-1 ring-white/10 shadow-[0_30px_70px_-30px_rgba(0,0,0,0.9)] transition-shadow hover:ring-gold/50">
              <img {...responsive(IMAGES[k], b.images.length === 2 ? "(min-width: 1024px) 560px, 100vw" : "(min-width: 1024px) 380px, 100vw", [480, 800, 1200])} alt={b.alts?.[i] || post.title} loading="lazy" decoding="async"
                   style={b.positions?.[i] ? { objectPosition: b.positions[i] } : undefined}
                   className={`${b.natural ? "aspect-video h-auto" : b.images.length === 2 ? "h-[260px] md:h-[400px]" : "h-[320px] md:h-[540px]"} w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-[1.04]`} />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent opacity-80" />
              <span className="pointer-events-none absolute bottom-3 right-3 rounded-sm bg-obsidian/40 p-1.5 backdrop-blur-sm"><CrownMark className="h-5" animate={false} /></span>
            </div>
          ))}
        </div>
        <figcaption className="mt-4 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{b.caption}</figcaption>
      </figure>
    );
  if (b.type === "gallery")
    return (
      <figure className={`my-12 ${post.theme === "electric" ? "rounded-lg border border-gold/30 bg-gold/[0.04] p-3 shadow-[0_0_56px_rgba(212,175,55,0.10)]" : ""}`} data-testid="blog-gallery">
        <div className={b.images.length === 3 ? "grid grid-cols-3 gap-3" : `grid grid-cols-2 gap-3 ${b.images.length === 4 ? "md:grid-cols-4" : "md:grid-cols-5"}`}>
          {b.images.map((k, i) => (
            <div key={k} className={`overflow-hidden rounded-lg border ${post.theme === "electric" ? "border-gold/25" : "border-white/10"} ${b.images.length === 5 && i === 4 ? "col-span-2 md:col-span-1" : ""}`}>
              <img {...responsive(IMAGES[k], b.images.length === 3 ? "(min-width: 1024px) 260px, 33vw" : `(min-width: 1024px) ${Math.round(800 / b.images.length)}px, 50vw`, [320, 480, 800])} alt={b.alts?.[i] || post.title} loading="lazy" decoding="async"
                   style={b.positions?.[i] ? { objectPosition: b.positions[i] } : undefined}
                   className="h-[200px] md:h-[360px] w-full object-cover transition-transform duration-[1.2s] hover:scale-105" />
            </div>
          ))}
        </div>
        <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{b.caption}</figcaption>
      </figure>
    );
  if (b.type === "table")
    return (
      <div className="mt-8 overflow-x-auto rounded-lg border border-white/10" data-testid="blog-table">
        <table className="w-full min-w-[620px] border-collapse text-left">
          <thead>
            <tr className="bg-[color:var(--accent-soft)]">
              {b.head.map((h) => (
                <th key={h} className="px-5 py-4 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {b.rows.map((r) => (
              <tr key={r[0]} className="border-t border-white/10 align-top hover:bg-white/[0.02] transition-colors">
                <td className="px-5 py-5 font-heading font-bold text-lg text-white whitespace-nowrap">{r[0]}</td>
                {r.slice(1).map((c) => (
                  <td key={c} className="px-5 py-5 text-sm text-zinc-300">{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  if (b.type === "palettes")
    return (
      <div className="mt-10" data-testid="blog-palettes">
        <div className="flex flex-col items-center gap-3 mb-8">
          <CrownMark className="h-16 md:h-20" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Signature palettes · line-marked by Speedy</span>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
        {b.items.map((p) => (
          <div key={p.name} className="group relative overflow-hidden rounded-lg border border-white/10 bg-obsidian-800 transition-colors hover:border-gold/50">
            <div className="flex h-24">
              {p.colors.map((c) => <span key={c} className="flex-1 transition-transform duration-700 group-hover:scale-y-110" style={{ background: c }} />)}
            </div>
            <div className="absolute right-3 top-3 rounded-sm bg-obsidian/70 p-1.5 backdrop-blur"><CrownMark className="h-6" animate={false} /></div>
            <div className="p-5">
              <p className="font-heading font-bold text-lg">{p.name}</p>
              <p className="mt-2 text-sm leading-relaxed text-zinc-300">{p.text}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {p.swatches.map((s) => <span key={s} className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{s}</span>)}
              </div>
            </div>
          </div>
        ))}
        </div>
      </div>
    );
  if (b.type === "steps")
    return (
      <ol className="mt-8 space-y-3">
        {b.items.map((s, i) => (
          <li key={s.title} className="group flex items-start gap-5 rounded-lg border border-white/10 bg-obsidian-800 p-6 hover:border-[color:var(--accent)] transition-colors">
            <span className="font-heading font-black text-3xl text-zinc-700 group-hover:text-[color:var(--accent)] transition-colors">
              {String(i + 1).padStart(2, "0")}
            </span>
            <div>
              <h3 className="font-heading font-bold text-lg">{s.title}</h3>
              <p className="mt-1 text-zinc-300">{s.text}</p>
            </div>
          </li>
        ))}
      </ol>
    );
  return null;
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = postBySlug(slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useEffect(() => { if (post) api.post("/blog/view", { slug }).catch(() => {}); }, [slug, post]);

  if (!post) return (
    <div className="pt-40 pb-24 text-center">
      <h1 className="font-heading font-black text-4xl">Article not found</h1>
      <Link to="/blog-articles" className="mt-6 inline-block text-neon">← Back to the blog</Link>
    </div>
  );

  const toc = post.sections.filter((s) => s.type === "h2" || s.type === "assess");
  const royal = post.theme === "royal";
  const electric = post.theme === "electric";
  const themed = royal || electric;
  const kenburns = !post.heroVideo && post.heroMotion === "kenburns";
  const shareUrl = `${DOMAIN}/blog-articles/${post.slug}`;
  const [titleA, titleB] = themed && post.title.includes(":") ? post.title.split(/:(.+)/).filter(Boolean) : [post.title];

  return (
    <article className={`blog-article min-h-screen noise ${royal ? "post-royal" : ""}`} data-testid={`blog-post-${post.slug}`}>
      <Seo
        title={`${post.seoTitle} | Speedy Resurfacing`}
        description={post.seoDescription}
        path={`/blog-articles/${post.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BlogPosting",
              headline: post.title,
              description: post.seoDescription,
              datePublished: post.date,
              dateModified: post.date,
              inLanguage: "en-AU",
              keywords: post.tags.join(", "),
              articleSection: post.category,
              url: `${DOMAIN}/blog-articles/${post.slug}`,
              mainEntityOfPage: { "@type": "WebPage", "@id": `${DOMAIN}/blog-articles/${post.slug}` },
              author: { "@type": "Organization", name: "Speedy Resurfacing", url: DOMAIN, sameAs: [INSTAGRAM_URL, FACEBOOK_URL] },
              publisher: {
                "@type": "Organization", name: "Speedy Resurfacing", url: DOMAIN, sameAs: [INSTAGRAM_URL, FACEBOOK_URL],
                logo: { "@type": "ImageObject", url: `${DOMAIN}/brand/speedy-resurfacing-logo.png` },
              },
              about: post.keyTakeaways,
              speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "[data-testid='blog-takeaways']"] },
            },
            {
              "@type": "FAQPage",
              mainEntity: post.faqs.map((f) => ({
                "@type": "Question", name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "LocalBusiness",
              name: "Speedy Resurfacing",
              telephone: "0466 495 495",
              email: "info@speedyresurfacing.com.au",
              url: DOMAIN,
              sameAs: [INSTAGRAM_URL, FACEBOOK_URL],
              address: { "@type": "PostalAddress", streetAddress: "545 Merrylands Road", addressLocality: "Merrylands",
                         addressRegion: "NSW", postalCode: "2160", addressCountry: "AU" },
              areaServed: SERVICE_AREAS,
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: DOMAIN + "/" },
                { "@type": "ListItem", position: 2, name: "Blog Articles", item: DOMAIN + "/blog-articles" },
                { "@type": "ListItem", position: 3, name: post.title, item: `${DOMAIN}/blog-articles/${post.slug}` },
              ],
            },
          ],
        }}
      />

      {/* Hero */}
      <header className="relative overflow-hidden pt-36 pb-16">
        <img {...responsive(IMAGES[post.hero], "100vw", [640, 1000, 1200, 1600])} fetchpriority="high" decoding="async" alt={post.title}
             data-testid={kenburns ? "blog-hero-kenburns" : "blog-hero-image"}
             className={`absolute inset-0 h-full w-full object-cover ${kenburns ? "kenburns opacity-[0.42]" : royal ? "opacity-[0.45]" : electric ? "opacity-[0.38]" : "opacity-[0.22]"}`} />
        {post.heroVideo && (
          <video autoPlay muted loop playsInline preload="auto" poster={IMAGES[post.hero]} data-testid="blog-hero-video"
                 className="absolute inset-0 h-full w-full object-cover opacity-[0.55] motion-reduce:hidden" aria-hidden="true">
            <source src={post.heroVideo} type="video/mp4" />
            {post.heroVideoWebm && <source src={post.heroVideoWebm} type="video/webm" />}
          </video>
        )}
        {post.heroVideo && <div className="absolute inset-0 bg-obsidian/40" />}
        {royal && <div className="absolute inset-0 royal-hero" />}
        {electric && <div className="absolute inset-0 electric-stage" />}
        <div className={`absolute inset-0 bg-gradient-to-t ${royal ? "from-obsidian via-obsidian/65 to-royal-deep/30" : electric ? "from-obsidian via-obsidian/85 to-obsidian/45" : "from-obsidian via-obsidian/80 to-obsidian/50"}`} />
        <CrownWatermark opacity={0.07} sizes="(min-width: 1024px) 26vw, 60vw"
          className="hidden sm:block right-[-8%] bottom-[-6%] w-[60vw] max-w-none lg:right-[2%] lg:bottom-[-4%] lg:w-[26vw] lg:max-w-[480px]" testid="blog-post-crown-watermark" />
        <div className="relative mx-auto max-w-4xl px-5 md:px-10">
          <nav className="flex flex-wrap items-center gap-2 font-mono text-xs text-zinc-500" data-testid="blog-breadcrumb">
            <Link to="/" className="hover:text-neon">Home</Link><span>/</span>
            <Link to="/blog-articles" className="hover:text-neon">Blog Articles</Link><span>/</span>
            <span className="text-zinc-300">{post.category}</span>
          </nav>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <PostBadge post={post} />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{post.dateLabel} · {post.readTime}</span>
          </div>
          <h1 className={`mt-6 font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tighter ${electric ? "lux-white" : ""}`}>
            {titleA}{titleB && <>:<br /><span className={electric ? "electric-text" : "royal-text"}>{titleB.trim()}</span></>}
          </h1>
          {royal && <RoyalRule />}
          {electric && <RoyalRule label="Electric Blue Series" />}
          <p className="mt-6 max-w-2xl text-lg text-zinc-300">{post.dek}</p>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4">
            <ShareBar url={shareUrl} title={post.title} testid="blog-share-top" />
            <a href={INSTAGRAM_URL} target="_blank" rel="me noopener noreferrer" data-testid="blog-instagram-follow-top"
               className="inline-flex h-11 items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 font-mono text-[10px] uppercase tracking-[0.18em] text-gold transition-colors duration-200 hover:bg-gold/20 hover:border-gold">
              <Instagram size={16} /> Follow {INSTAGRAM_HANDLE}
            </a>
            <a href={FACEBOOK_URL} target="_blank" rel="me noopener noreferrer" data-testid="blog-facebook-follow-top"
               className="inline-flex h-11 items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 font-mono text-[10px] uppercase tracking-[0.18em] text-gold transition-colors duration-200 hover:bg-gold/20 hover:border-gold">
              <Facebook size={16} /> Follow us on Facebook
            </a>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 md:px-10 pb-24">
        <div className="lg:grid lg:grid-cols-[210px_1fr] lg:gap-14">
          {/* Sticky TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 border-l border-white/10 pl-5" data-testid="blog-toc">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">In this article</span>
              <ul className="mt-4 space-y-3">
                {toc.map((h, i) => (
                  <li key={h.id}>
                    <a href={`#${h.id}`} data-testid={`blog-toc-${i}`}
                       className="block text-sm leading-snug text-zinc-500 hover:text-gold transition-colors">
                      {h.text}
                    </a>
                  </li>
                ))}
                <li>
                  <a href="#enquiry" className="block text-sm leading-snug text-neon hover:text-neon-hover">Request an assessment</a>
                </li>
              </ul>
            </div>
          </aside>

          {/* Article body */}
          <div className="max-w-3xl">
            {/* Key takeaways — GEO / AI answer engines */}
            <Reveal>
              <div data-testid="blog-takeaways" className={`rounded-lg p-7 md:p-8 ${electric ? "border border-gold/30 border-l-2 border-l-gold bg-gold/[0.06] shadow-[0_0_48px_rgba(212,175,55,0.10)]" : "border-l-2 border-[color:var(--accent)] bg-obsidian-800"}`}>
                <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                  <Sparkles size={13} /> Key takeaways
                </span>
                <ul className="mt-5 space-y-3">
                  {post.keyTakeaways.map((k) => (
                    <li key={k} className="flex gap-3 text-sm md:text-base text-zinc-200">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--accent)]" /> {k}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {post.sections.map((b, i) => <Block key={i} b={b} post={post} />)}

            {/* FAQ */}
            <div className="mt-16">
              <h2 className="border-t border-[color:var(--rule)] pt-8 font-heading font-black text-2xl md:text-3xl tracking-tight">
                Frequently Asked Questions
              </h2>
              <Accordion type="single" collapsible className="mt-6 rounded-lg border border-white/10 bg-obsidian-800 px-6" data-testid="blog-faq">
                {post.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`} className="border-white/10">
                    <AccordionTrigger className="text-left font-heading font-semibold hover:text-neon">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-zinc-300 leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* Share */}
            <div className="mt-12 flex flex-col gap-4 rounded-lg border border-white/10 bg-obsidian-800 p-6 md:flex-row md:items-center md:justify-between" data-testid="blog-share-box">
              <div>
                <p className="font-heading font-bold text-lg">Know a school, club or court owner who needs this?</p>
                <p className="mt-1 text-sm text-zinc-400">
                  Send them the article in one tap, and follow Speedy Resurfacing on{" "}
                  <a href={INSTAGRAM_URL} target="_blank" rel="me noopener noreferrer" data-testid="blog-instagram-follow-bottom"
                     className="font-semibold text-gold hover:text-gold-hover underline-offset-4 hover:underline">
                    Instagram {INSTAGRAM_HANDLE}
                  </a>{" "}
                  and{" "}
                  <a href={FACEBOOK_URL} target="_blank" rel="me noopener noreferrer" data-testid="blog-facebook-follow-bottom"
                     className="font-semibold text-gold hover:text-gold-hover underline-offset-4 hover:underline">
                    Facebook
                  </a>{" "}
                  for before-and-after court transformations across Sydney.
                </p>
              </div>
              <ShareBar url={shareUrl} title={post.title} compact testid="blog-share-bottom" />
            </div>

            {/* Enquiry form */}
            <div id="enquiry" className="mt-16 scroll-mt-28">
              <AssessmentForm testid="blog-assessment-form" source={post.slug} />
            </div>

            {/* Keep reading */}
            <div className="mt-14 grid sm:grid-cols-2 gap-4">
              <Link to="/method" className="group rounded-lg border border-white/10 bg-obsidian-800 p-7 hover:border-gold/40 transition-colors" data-testid="blog-keep-reading">
                <span className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Keep reading</span>
                <p className="mt-2 font-heading font-bold text-xl group-hover:text-neon transition-colors">The Speedy Method</p>
              </Link>
              <Link to="/gallery" className="group rounded-lg border border-white/10 bg-obsidian-800 p-7 hover:border-gold/40 transition-colors sm:text-right" data-testid="blog-see-the-work">
                <span className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500">See the work</span>
                <p className="mt-2 flex items-center gap-2 sm:justify-end font-heading font-bold text-xl group-hover:text-neon transition-colors">
                  Finished Courts <ArrowUpRight size={18} />
                </p>
              </Link>
            </div>

            <Link to="/blog-articles" className="mt-10 inline-flex items-center gap-2 font-bold text-gold hover:text-gold-hover">
              All articles <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}
